import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Calendar, FileSpreadsheet } from 'lucide-react';
import toast from "react-hot-toast";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterSelect,
  masterInput
} from '../../GlobalManagement/Pages/masterPageStyles';

const LeaveHistory = () => {
  const [leaves, setLeaves] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18); // Matches Department Master page size

  const adminData = JSON.parse(localStorage.getItem("adminData") || "{}");
  const loggedInUserId = adminData.userId || adminData.employeeUserId || adminData._id || "";

  const fetchLeaveHistory = async () => {
    try {
      const res = await axios.get("/api/leave-application");
      const actualData = Array.isArray(res.data)
        ? res.data
        : res.data.data || res.data.leaves || [];
      setLeaves(actualData);
    } catch (error) {
      toast.error("Failed to fetch leave history");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get("/api/departments");
      setDepartments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error("Failed to fetch departments");
    }
  };

  useEffect(() => {
    fetchLeaveHistory();
    fetchDepartments();
  }, []);

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-") : "-";

  const formatDateTime = (date) =>
    date ? new Date(date).toLocaleString("en-GB").replace(/\//g, "-") : "-";

  const parseDate = (value) => {
    if (!value) return null;
    if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
      const [dd, mm, yyyy] = value.split("-");
      const d = new Date(`${yyyy}-${mm}-${dd}`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getFinalStatus = (leave) =>
    String(leave.approveRejectedStatus || leave.applyStatus || "PENDING").toUpperCase();

  const getLeaveCode = (leaveType = "") => {
    const type = String(leaveType || "").toUpperCase();
    if (type.includes("SICK") || type === "SL") return "SL";
    if (type.includes("CASUAL") || type === "CL") return "CL";
    return type || "-";
  };

  const getStatusClass = (status = "") => {
    const s = String(status || "").toUpperCase();
    if (s === "APPROVED") return "bg-green-100 text-green-700 border-green-300";
    if (s === "REJECTED") return "bg-red-100 text-red-700 border-red-300";
    return "bg-yellow-100 text-yellow-700 border-yellow-300";
  };

  const normalizeRoleToken = (value) =>
    String(value || "").toUpperCase().replace(/[^A-Z]/g, "");

  const isRMRole = (role) => {
    const r = normalizeRoleToken(role);
    return r.includes("REPORTINGMANAGER") || r === "RM" || r.startsWith("RM") || r.includes("RMDH");
  };

  const isDHRole = (role) => {
    const r = normalizeRoleToken(role);
    return r.includes("DEPARTMENTHEAD") || r === "DH" || r.endsWith("DH") || r.includes("RMDH");
  };

  const getByRole = (history = [], role) => {
    const list = Array.isArray(history) ? history.slice() : [];
    list.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    if (role === "Reporting Manager") return list.find((h) => isRMRole(h.role)) || {};
    if (role === "Department Head") return list.find((h) => isDHRole(h.role)) || {};
    return list.find((h) => h.role === role) || {};
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter((leave) => {
      const finalStatus = getFinalStatus(leave);
      const leaveDate = parseDate(leave.applicationDate || leave.fromDate);
      const monthStr = leaveDate
        ? `${leaveDate.getFullYear()}-${String(leaveDate.getMonth() + 1).padStart(2, "0")}`
        : "";

      const monthMatch = !selectedMonth || monthStr === selectedMonth;
      const statusMatch = selectedStatus === "ALL" || finalStatus === selectedStatus;
      const departmentMatch =
        selectedDepartment === "ALL" || (leave.departmentName || "") === selectedDepartment;

      const q = String(searchTerm || "").toUpperCase().trim();
      const name = String(leave.employeeName || "").toUpperCase();
      const empId = String(leave.employeeId || "").toUpperCase();
      const empUserId = String(leave.employeeUserId || "").toUpperCase();
      const matchesSearch = !q || name.includes(q) || empId.includes(q) || empUserId.includes(q);

      return monthMatch && statusMatch && departmentMatch && matchesSearch;
    });
  }, [leaves, selectedMonth, selectedStatus, selectedDepartment, searchTerm]);

  const paginatedLeaves = useMemo(() => {
    if (pageSize === -1) return filteredLeaves;
    const start = (currentPage - 1) * pageSize;
    return filteredLeaves.slice(start, start + pageSize);
  }, [filteredLeaves, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonth, selectedStatus, selectedDepartment, pageSize]);

  const handleExportExcel = () => {
    try {
      const exportData = filteredLeaves.map((leave, index) => {
        const rm = getByRole(leave.history, "Reporting Manager");
        const dh = getByRole(leave.history, "Department Head");
        return {
          "SL No": index + 1,
          "Emp ID": String(leave.employeeId || ""),
          "Emp Name": String(leave.employeeName || ""),
          "Department": String(leave.departmentName || "-"),
          "Apply Date": String(formatDate(leave.applicationDate)),
          "Leave Type": String(getLeaveCode(leave.leaveType)),
          "From Date": String(formatDate(leave.fromDate)),
          "To Date": String(formatDate(leave.toDate)),
          "Total Days": String(leave.noOfDays ?? ""),
          "RM": String(leave.reportingManager || ""),
          "RM Status": String(rm.status || "-"),
          "RM Date & Time": String(formatDateTime(rm.date)),
          "DH": String(leave.departmentHead || ""),
          "DH Status": String(dh.status || "-"),
          "DH Date & Time": String(formatDateTime(dh.date)),
          "Final Status": String(getFinalStatus(leave)),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leave History");
      XLSX.writeFile(workbook, "LeaveHistory.xlsx");
      toast.success("Excel exported successfully");
    } catch (error) {
      toast.error("Failed to export Excel");
    }
  };

  const updateLeaveDecision = async (leaveId, roleKey, status) => {
    try {
      const res = await axios.put(`/api/leave-application/${leaveId}/status`, {
        status,
        loggedInUserId,
        decisionRole: roleKey,
      });
      const updated = res.data?.leave;
      if (updated) {
        setLeaves((prev) => prev.map((l) => (l._id === updated._id ? updated : l)));
      } else {
        fetchLeaveHistory();
      }
      toast.success(`Status updated`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Master Header */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Calendar size={14} /></div>
              <div><h1 className={masterTitle}>Employee Leave History</h1></div>
            </div>
            <button 
              type="button"
              onClick={handleExportExcel}
              className={masterAccentButton}
            >
              <FileSpreadsheet size={10} /> EXPORT EXCEL
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex-1 flex flex-col min-h-0 overflow-hidden`}>
          <div className={`${masterPanel} flex flex-col overflow-hidden w-full h-full`}>
            
            {/* SearchBar & Additional Filters Toolbar */}
            <div className="border-b border-slate-200 flex-shrink-0 bg-white p-2 flex flex-col gap-2">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search by name, emp ID or user ID..." 
                totalItems={filteredLeaves.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />

              <MobileHeaderToggle>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className={masterInput}
                  />

                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className={masterSelect}
                  >
                    <option value="ALL">All Final Status</option>
                    <option value="PENDING">PENDING</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                  </select>

                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className={masterSelect}
                  >
                    <option value="ALL">All Departments</option>
                    {departments.map((d) => (
                      <option key={d._id} value={d.deptName}>
                        {d.deptName}
                      </option>
                    ))}
                  </select>
                </div>
              </MobileHeaderToggle>
            </div>

            {/* Table Container */}
            <div className="w-full flex-1 min-h-0 overflow-x-auto" id="leave-history-print">
              <table className={masterTable}>
                <thead className={masterStickyHeadRow}>
                  <tr>
                    <th className={masterTableData}>SL No</th>
                    <th className={masterTableData}>Emp ID</th>
                    <th className={masterTableData}>Emp Name</th>
                    <th className={masterTableData}>User ID</th>
                    <th className={masterTableData}>Department</th>
                    <th className={masterTableData}>Apply Date</th>
                    <th className={masterTableData}>Leave Type</th>
                    <th className={masterTableData}>From Date</th>
                    <th className={masterTableData}>To Date</th>
                    <th className={masterTableData}>Total Days</th>
                    <th className={masterTableData}>RM</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Date & Time</th>
                    <th className={masterTableData}>DH</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Date & Time</th>
                    <th className={masterTableData}>Final Status</th>
                  </tr>
                </thead>

                <tbody className={masterTbody}>
                  {loading ? (
                    <tr>
                      <td colSpan="17" className="py-8 text-slate-500 font-semibold text-xs uppercase">Loading...</td>
                    </tr>
                  ) : paginatedLeaves.length > 0 ? (
                    paginatedLeaves.map((leave, index) => {
                      const rm = getByRole(leave.history, "Reporting Manager");
                      const dh = getByRole(leave.history, "Department Head");
                      const rmStatus = String(leave.reportingManagerApproval || rm.status || "PENDING").toUpperCase();
                      const dhStatus = String(leave.departmrntHeadApproval || dh.status || "PENDING").toUpperCase();
                      const finalStatus = getFinalStatus(leave);

                      return (
                        <tr key={leave._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className={masterTableCell}>{leave.employeeId}</td>
                          <td className={masterTableCell}>{leave.employeeName}</td>
                          <td className={masterTableCell}>{leave.employeeUserId || "-"}</td>
                          <td className={masterTableCell}>{leave.departmentName || "-"}</td>
                          <td className={masterTableCell}>{formatDate(leave.applicationDate)}</td>
                          <td className={masterTableCell}>{getLeaveCode(leave.leaveType)}</td>
                          <td className={masterTableCell}>{formatDate(leave.fromDate)}</td>
                          <td className={masterTableCell}>{formatDate(leave.toDate)}</td>
                          <td className={masterTableCell}>{leave.noOfDays}</td>
                          <td className={masterTableCell}>{leave.reportingManager}</td>
                          <td className={masterTableCell}>
                            <select
                              value={rmStatus}
                              onChange={(e) => updateLeaveDecision(leave._id, "RM", e.target.value)}
                              className={`border rounded px-1 py-0.5 text-[10px] font-semibold uppercase ${getStatusClass(rmStatus)}`}
                            >
                              <option value="PENDING">PENDING</option>
                              <option value="APPROVED">APPROVED</option>
                              <option value="REJECTED">REJECTED</option>
                            </select>
                          </td>
                          <td className={masterTableCell}>{formatDateTime(rm.date)}</td>
                          <td className={masterTableCell}>{leave.departmentHead}</td>
                          <td className={masterTableCell}>
                            <select
                              value={dhStatus}
                            onChange={(e) => updateLeaveDecision(leave._id, "DH", e.target.value)}
                              className={`border rounded px-1 py-0.5 text-[10px] font-semibold uppercase ${getStatusClass(dhStatus)}`}
                            >
                              <option value="PENDING">PENDING</option>
                              <option value="APPROVED">APPROVED</option>
                              <option value="REJECTED">REJECTED</option>
                            </select>
                          </td>
                          <td className={masterTableCell}>{formatDateTime(dh.date)}</td>
                          <td className={masterTableCell}>
                            <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase ${getStatusClass(finalStatus)}`}>
                              {finalStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="17" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default LeaveHistory;